import { t } from "i18next";
import axios from "axios";
import { isEmpty } from "lodash";
import { apiURL } from "../Utils/Const";
import { DEVICE_ID_HEADER, getDeviceId } from "../Utils/deviceId";

export const apiBaseUrl = `${apiURL}`;

const validateResponse = (res) => {
  const { data } = res;
  if (
    data &&
    (data.status === "SUCCESS" ||
      data.status === "CREATED" ||
      data.status === "FAILED")
  ) {
    return data;
  } else {
    const errorMessage =
      data && data.message ? data.message : t("api.unknownError");
    throw new Error(errorMessage);
  }
};

const extractErrMsg = (err) => {
  if (err) {
    const { message } = err.response?.data;
    if (message) {
      return message;
    }
    return "someThingWentWrong";
  }
};

// True only when the stored JWT is present AND past its `exp`. Used to tell a
// genuinely dead/stale token apart from the backend's spurious 511s. Returns
// false on anything unparseable so ambiguity never force-logs-out a valid user.
const isTokenExpired = () => {
  try {
    const { token } = JSON.parse(localStorage.getItem("userData")) || {};
    if (!token) return false;
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(b64));
    return typeof exp === "number" && exp * 1000 < Date.now();
  } catch {
    return false;
  }
};

const handleErr = (err) => {
  // A genuine 401 always kills the session. The backend ALSO returns 511 for a
  // dead/expired token — but it emits 511 spuriously on some valid authenticated
  // calls too (wishlist, checkout), and a blanket 511-logout kicked users out
  // mid-flow. So on 511 we only tear down the session when the stored JWT is
  // actually expired (the real "stale token" case); a 511 on a still-valid token
  // is the spurious kind and is left alone.
  if (err?.status === 401 || (err?.status === 511 && isTokenExpired())) {
    localStorage.removeItem("userData");
    window.location.reload();
  }
  return extractErrMsg(err);
};

const api = axios.create({
  baseURL: apiBaseUrl,
});

// Tells the backend which language to answer in. ApiResponseTranslationAdvice translates
// ApiResponse.message only when this header is present, so a request without it keeps the exact
// English responses the storefront has always had -- which is what makes this safe to add
// everywhere at once. i18nextLng is where LanguageSwitcher persists the choice, and App.js also
// writes it from the store's own configured language.
const updateLanguageHeader = () => {
  const lang = localStorage.getItem("i18nextLng");
  if (lang) {
    // Strip any region ("hi-IN" -> "hi"): the backend matches on language alone.
    api.defaults.headers.common["Accept-Language"] = lang.split("-")[0];
    return;
  }
  delete api.defaults.headers.common["Accept-Language"];
};

// upload() and fileUpload() build their own headers instead of going through `api`, so the
// axios default set above never reaches them. Same value, applied by hand.
const languageHeader = () => {
  const lang = localStorage.getItem("i18nextLng");
  return lang ? { "Accept-Language": lang.split("-")[0] } : {};
};

const updateHeader = () => {
  updateLanguageHeader();

  const user = JSON.parse(localStorage.getItem("userData")) || {};
  const session = sessionStorage.getItem("sessionToken") || null;
  if (!isEmpty(user)) {
    const { token } = user;
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    api.defaults.headers.common["session_token"] = "";
  } else {
    api.defaults.headers.common["session_token"] = session;
    api.defaults.headers.common["Authorization"] = "";
  }
  // api.defaults.headers.common["user_name"] = "dev-iron-hill-fitness";

  // Stable per-browser id for Vertex search events. Set here, alongside the other headers,
  // because every get/post/put/del below already funnels through updateHeader() — a request
  // interceptor would be a second mechanism doing the same job.
  //
  // Unlike session_token this is NOT cleared on login: the whole point is that it spans the
  // guest-then-login journey. See Utils/deviceId.js.
  //
  // Wrapped even though getDeviceId() already swallows everything. This function runs before
  // EVERY api call, so anything thrown here breaks the entire storefront, not just events —
  // and "telemetry can never break the app" is worth two guards rather than a shared
  // assumption between two files.
  try {
    const deviceId = getDeviceId();
    if (deviceId) {
      api.defaults.headers.common[DEVICE_ID_HEADER] = deviceId;
    } else {
      // Consent withdrawn or storage unreadable. Delete rather than send "" — a blank header
      // would still reach the backend and read as "present but empty".
      delete api.defaults.headers.common[DEVICE_ID_HEADER];
    }
  } catch (e) {
    delete api.defaults.headers.common[DEVICE_ID_HEADER];
  }
};

export const unAuthGet = async (endpoint) => {
  updateHeader();
  try {
    const res = await api.get(endpoint);
    return validateResponse(res);
  } catch (err) {
    handleErr(err);
    throw err;
  }
};

export const get = async (endpoint) => {
  updateHeader();
  try {
    const res = await api.get(endpoint);
    return validateResponse(res);
  } catch (err) {
    handleErr(err);
    throw err;
  }
};

export const post = async (endpoint, payload) => {
  updateHeader();
  try {
    const res = await api.post(endpoint, payload);
    return validateResponse(res);
  } catch (err) {
    handleErr(err);
    throw err;
  }
};

// Updated Put function
export const put = async (endpoint, payload) => {
  updateHeader();
  try {
    const res = await api.put(endpoint, payload);
    return validateResponse(res);
  } catch (err) {
    handleErr(err);
    throw err;
  }
};

// Updated Remove function
export const remove = async (endpoint, payload) => {
  updateHeader();
  try {
    const res = await api.delete(endpoint, { data: payload });
    return validateResponse(res);
  } catch (err) {
    handleErr(err);
    throw err;
  }
};

export const upload = (endpoint, payload) => {
  return new Promise((resolve, reject) => {
    const user = JSON.parse(localStorage.getItem("userData")) || {};
    if (!user) reject("Unauthorized User");
    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${user.token}`);
    Object.entries(languageHeader()).forEach(([k, v]) =>
      myHeaders.append(k, v),
    );

    fetch(`${apiURL}/${endpoint}`, {
      method: "POST",
      body: payload,
      headers: myHeaders,
      redirect: "follow",
    })
      .then((response) => response.json())
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        handleErr(err);
      });
  });
};

export const fileUpload = (endpoint, payload, onProgress) => {
  return new Promise((resolve, reject) => {
    const user = JSON.parse(localStorage.getItem("userData")) || {};
    if (!user) return reject("Unauthorized User");
    axios
      .post(`${apiBaseUrl}${endpoint}`, payload, {
        headers: {
          Authorization:
            user.token !== undefined ? `Bearer ${user.token}` : null,
          "Content-Type": "multipart/form-data",
          ...languageHeader(),
        },
        onUploadProgress: (progressEvent) => {
          // Calculate upload progress percentage
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );

          if (onProgress) onProgress(percentCompleted); // Invoke progress callback
        },
      })
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        handleErr(error);
        // reject(error.response?.data || error.message);
      });
  });
};
